import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AppContext } from '@@/contexts/AppContext';
import { AuthContext } from '@@/contexts/AuthContext';
import { InsightsApiClient, GetWisdomTranscriptsPayload, GetWisdomTranscriptsResponse } from '../../api/api-client';
import { ResultValue } from '@@/types/ApiResult';

export const useWisdomTranscripts = (parameters: GetWisdomTranscriptsPayload) => {
  const appContext = useContext(AppContext);
  const { instanceData } = useContext(AuthContext);

  const fetchLog = async () => {
    if (!appContext) {
      throw new Error('App context is not available');
    }

    if (!instanceData) {
      throw new Error('Instance data is not available');
    }

    const client = new InsightsApiClient();
    const result = await client.getWisdomTranscripts({
      instanceId: instanceData.instanceId,
      region: instanceData.region,
      assistantId: parameters.assistantId,
      sessionId: parameters.sessionId
    });

    if (!ResultValue.ok(result)) {
      throw new Error(result?.message || 'Failed to fetch wisdom transcripts');
    }

    return result.data;
  };

  const {
    data,
    isLoading,
    error: apiError,
    isRefetching,
    refetch
  } = useQuery<GetWisdomTranscriptsResponse, Error>({
    queryKey: ['log', instanceData?.instanceId, instanceData?.region, parameters],
    queryFn: fetchLog,
    enabled: !!(instanceData?.instanceId && instanceData?.region && parameters.assistantId && parameters.sessionId),
    refetchOnWindowFocus: false,
    staleTime: 0,
    retry: 0
  });

  return {
    data,
    isLoading,
    isRefetching,
    error: apiError,
    refetch
  };
};
